/**
 * Var enhetens grunduppgifter kommer ifrån.
 *
 * Skolenheterna kommer från Skolverkets skolenhetsregister. Förskolorna gör
 * det inte — de kommer från Kolada (RKA) med adress och koordinat från SCB och
 * har aldrig funnits i Skolverkets register. Alla källtexter på profilen läser
 * härifrån så att påståendet stämmer på varje sida.
 */

export function isPreschoolUnit(educationTypes?: string[] | null): boolean {
  return (educationTypes ?? []).some((t) => /^förskola$/i.test(t.trim()));
}

/** Kort källnamn, t.ex. i meta-beskrivningar: "Skolverket" / "Kolada (RKA) och SCB". */
export function sourceShortLabel(educationTypes?: string[] | null): string {
  return isPreschoolUnit(educationTypes) ? "Kolada (RKA) och SCB" : "Skolverket";
}

/** Fullt registernamn: "Skolverkets skolenhetsregister" / "Koladas enhetsregister". */
export function sourceRegisterLabel(educationTypes?: string[] | null): string {
  return isPreschoolUnit(educationTypes)
    ? "Koladas enhetsregister (RKA), adress från SCB"
    : "Skolverkets skolenhetsregister";
}

/** Ledtext ovanför rubriken i heron. */
export function sourceEyebrow(educationTypes?: string[] | null): string {
  return isPreschoolUnit(educationTypes)
    ? "Grunddata från Kolada (RKA)"
    : "Grunddata från Skolverket";
}

/**
 * Böjning efter skolform. En förskolechef märker direkt om vi kallar hennes
 * verksamhet "skolan" — därför böjs varje text i anspråksflödet härifrån.
 */
export function unitNoun(
  educationTypes?: string[] | null,
  form: "definite" | "indefinite" | "genitive" = "definite",
): string {
  const pre = isPreschoolUnit(educationTypes);
  if (form === "indefinite") return pre ? "förskola" : "skola";
  if (form === "genitive") return pre ? "förskolans" : "skolans";
  return pre ? "Förskolan" : "Skolan";
}

/** Gemen variant, t.ex. mitt i en mening: "…arbetar du på förskolan?" */
export function unitNounLower(
  educationTypes?: string[] | null,
  form: "definite" | "indefinite" | "genitive" = "definite",
): string {
  return unitNoun(educationTypes, form).toLowerCase();
}

/**
 * Källrad med datum under faktaraden: "Uppgifter från Skolverket, uppdaterade
 * 13 oktober 2026". Datumet är det som bygger förtroende. Utan giltigt datum
 * står bara källan.
 */
export function sourceDateLine(educationTypes: string[] | null | undefined, fetchedAt: string | null | undefined): string {
  const kalla = isPreschoolUnit(educationTypes) ? "Kolada (RKA) och SCB" : "Skolverket";
  const d = fetchedAt ? new Date(fetchedAt) : null;
  if (!d || Number.isNaN(d.getTime())) return `Uppgifter från ${kalla}`;
  const datum = d.toLocaleDateString("sv-SE", { day: "numeric", month: "long", year: "numeric", timeZone: "Europe/Stockholm" });
  return `Uppgifter från ${kalla}, uppdaterade ${datum}`;
}
